import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency, formatNumber, formatWithLocale } from '@adjust/dashboard-core/lib/format/number';
import { clamp } from 'lodash';
import {
  TileWithLoading,
  tileWithLoadingPropTypes,
} from '@react/Pages/AccountSettingsPage/YourDataSection/TileWithLoading';

const PLAN_TYPE_BASE = 'base';

const renderIncludedLabel = param => {
  switch (param) {
    case 'monthly_active_users':
      return t('label.pricing-plan-included') + ' ' + t('label.monthly-active-users');
    case 'data_points':
      return t('label.pricing-plan-included') + ' ' + t('label.adata-points');
    case 'attributions':
      return t('label.included-attributions');
  }
};

const renderAdditionalLabel = param => {
  switch (param) {
    case 'monthly_active_users':
      return t('label.pricing-plan-additional') + ' ' + t('label.monthly-active-users');
    case 'data_points':
      return t('label.pricing-plan-additional') + ' ' + t('label.adata-points');
    case 'attributions':
      return t('label.additional-attributions');
  }
};

const renderPlanType = value => {
  switch (value) {
    case 'core':
      return t('pricing-plan.plan-type.core');
    case 'base':
      return t('pricing-plan.plan-type.base');
    case 'basic':
      return t('pricing-plan.plan-type.basic');
    case 'business':
      return t('pricing-plan.plan-type.business');
    case 'business_pro':
      return t('pricing-plan.plan-type.business_pro');
    case 'enterprise':
      return t('pricing-plan.plan-type.enterprise');
    case 'flat':
      return t('pricing-plan.plan-type.flat');
    case 'pro':
      return t('pricing-plan.plan-type.pro');
    case 'pro_plus':
      return t('pricing-plan.plan-type.pro_plus');
  }
};

const PricingPlanTileBase = ({ currentAccount, currentCharge }) => {
  const account = currentAccount ?? {};
  const charge = currentCharge ?? {};
  const formatAttributionPrice = formatWithLocale({
    style: 'currency',
    currency: account.salesforce_order.currency,
    maximumFractionDigits: 8,
  });

  const progress = `${Math.round(
    clamp((charge.totalMetrics / account.salesforce_order.included_metric_amount) * 100, 100),
  )}%`;

  const metricPrice = Math.max(charge.totalMetrics - account.salesforce_order.included_metric_amount, 0);

  return (
    <>
      <div className="your-data-card__title">{t('label.pricing-plan')}</div>
      <div className="your-data-card__body">
        <div className="pricing-plan-container">
          <div className="attributions-usage">
            <div className="attributions-usage-left">{renderIncludedLabel(account.salesforce_order.metric_type)}</div>
            <div className="attributions-usage-right">
              {account.salesforce_order.included_metric_amount <= 0 ? (
                <span>{t('label.unlimited')}</span>
              ) : (
                <span>{formatNumber(account.salesforce_order.included_metric_amount)}</span>
              )}
            </div>
            <div className="attributions-usage-bar">
              {Object.keys(charge).length > 0 && account.salesforce_order.included_metric_amount > 0 && (
                <div className="attributions-usage-progress" style={{ width: progress }}>
                  {formatNumber(charge.totalMetrics)}
                </div>
              )}
            </div>
          </div>
          <div className="flex-box-row">
            <div className="flex-one">
              <p className="payment-plan-name">{renderPlanType(account.salesforce_order.plan_type)}</p>
              <p className="payment-plan-price">
                {formatCurrency({ currency: account.salesforce_order.currency })(
                  account.salesforce_order.total_order_amount,
                )}
              </p>
              <p className="payment-plan-description">
                {t('label.billing-cycle', { value: account.salesforce_order.license_billing_cycle })}
              </p>
            </div>
            {account.salesforce_order.included_metric_amount > 0 && (
              <div className="flex-one payment-plan-center">
                <p className="payment-plan-name">
                  {metricPrice}{' '}
                  <span className="payment-plan-light">
                    &times; {formatAttributionPrice(account.salesforce_order.metric_unit_price)}
                  </span>
                </p>
                <p className="payment-plan-price">
                  {formatCurrency({ currency: account.salesforce_order.currency })(
                    metricPrice * account.salesforce_order.metric_unit_price,
                  )}
                </p>
                <p className="payment-plan-description">
                  {renderAdditionalLabel(account.salesforce_order.metric_type)}
                </p>
              </div>
            )}

            <div className="flex-one payment-plan-right">
              <p className="payment-plan-name">&nbsp;</p>
              <p className="payment-plan-price">
                {account.salesforce_order.plan_type === PLAN_TYPE_BASE
                  ? formatCurrency({ currency: account.salesforce_order.currency })(0)
                  : formatCurrency({ currency: account.salesforce_order.currency })(
                      metricPrice * account.salesforce_order.metric_unit_price +
                        account.salesforce_order.total_order_amount,
                    )}
              </p>
              <p className="payment-plan-description">{t('label.total')}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

PricingPlanTileBase.propTypes = {
  currentAccount: PropTypes.shape({
    payment_plan: PropTypes.shape({
      base_price: PropTypes.number,
      attribution_price: PropTypes.number,
      included_attributions: PropTypes.number,
      name: PropTypes.string,
      yearly: PropTypes.bool,
    }),
    currency_iso_code: PropTypes.string,
  }),
  currentCharge: PropTypes.shape({
    totalAttributions: PropTypes.number,
    totalPriceNet: PropTypes.string,
  }),
};

const PricingPlanTile = TileWithLoading(PricingPlanTileBase);

PricingPlanTile.propTypes = {
  ...PricingPlanTileBase.propTypes,
  ...tileWithLoadingPropTypes,
};

export default PricingPlanTile;
